import React, { useState, useEffect, useMemo } from "react";
import { PropTypes } from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import AppHOC from "../../../generics/AppHOC";
import {
  setLocalStorageFlag,
  isBandForecastingTabSelected,
} from "../../../utils/common";
import {
  getADAccess,
  getBWFAccess,
  getPPRAccess,
} from "../../../utils/license";
import { getCookie } from "../../../serviceWorker";
import css from "../insightsMagneticStyle.less";
import { routeURLs } from "../../../utils/enums";
import useI18n from "../../../i18n/useI18n";

const [HbrTabGroup, HbrTab] = reactWrapper(["hbr-tab-group", "hbr-tab"]);
const PPR_TAB = "pprTab";
const BANDWIDTH_FORECASTING_TAB = "bandForecastingTab";
const ANOMALY_TAB = "anomalyTab";
const PREDICTIVE_TABS = [BANDWIDTH_FORECASTING_TAB, ANOMALY_TAB, PPR_TAB];
const PREDICTIVE_TAB_ROUTES = {
  [PPR_TAB]: routeURLs.pn,
  [BANDWIDTH_FORECASTING_TAB]: routeURLs.bwf,
  [ANOMALY_TAB]: routeURLs.ad,
};

const PredictiveNetworksTabs = (props) => {
  const { tViews } = useI18n("views");
  const location = useLocation();
  const navigate = useNavigate();
  const bwfAccess = useMemo(() => getBWFAccess(), []);
  const adAccess = useMemo(() => getADAccess(), []);
  const pprAccess = useMemo(() => getPPRAccess(), []);
  const currentPath = location.pathname;
  const normalizedPath = currentPath.replace(/\/+$/, "");
  const isAdRoute =
    normalizedPath.endsWith(routeURLs.ad) ||
    normalizedPath.includes(`${routeURLs.ad}/`);
  const isBwfRoute = normalizedPath.endsWith(routeURLs.bwf);
  const isPnRoute = normalizedPath.endsWith(routeURLs.pn);
  let routeSelectedTab = null;
  if (isAdRoute) {
    routeSelectedTab = ANOMALY_TAB;
  } else if (isBwfRoute && bwfAccess.available === true) {
    routeSelectedTab = BANDWIDTH_FORECASTING_TAB;
  } else if (isPnRoute) {
    routeSelectedTab = PPR_TAB;
  }
  const isBandwidthTab = isBandForecastingTabSelected();
  const initSelection =
    routeSelectedTab ||
    (isBandwidthTab && bwfAccess.available === true
      ? BANDWIDTH_FORECASTING_TAB
      : props.globalFilter.predictiveTabSelected);
  const isSecondaryTabsShown = bwfAccess.available || adAccess.available;
  const [selectedTab, setSelectedTab] = useState(initSelection);
  const isUIConvergence = getCookie("cl-analytics");
  const tabStyle = isUIConvergence
    ? "convergencePredictiveTab"
    : "predictiveNetworksTabs";
  const first20CharsUrl = window.location.href.substring(0, 20);
  //check if au region
  const isPredictivLabelEnabled = first20CharsUrl.includes("au");

  const setPredictiveTab = (predictiveTabSelected) => {
    props.setAltoProps({ predictiveTabSelected, status: "" });
  };

  const syncPredictiveTab = (tabValue) => {
    setLocalStorageFlag(
      "bandForecastingTab",
      tabValue === BANDWIDTH_FORECASTING_TAB,
    );
    setSelectedTab(tabValue);
    setPredictiveTab(tabValue);
  };

  const onTabsChange = (tabValue) => {
    const selectedPredictiveTab = PREDICTIVE_TABS.includes(tabValue)
      ? tabValue
      : PPR_TAB;

    syncPredictiveTab(selectedPredictiveTab);

    if (selectedPredictiveTab === ANOMALY_TAB) {
      // remove subTab cookies
      localStorage.removeItem("selectedADTab");
      localStorage.removeItem("selectedUsageADTab");
    }

    navigate(PREDICTIVE_TAB_ROUTES[selectedPredictiveTab]);

    setTimeout(() => {
      const tab = document.querySelector(
        `hbr-tab[panel="${selectedPredictiveTab}"]`,
      );
      (tab?.shadowRoot?.querySelector('div[part="base"]') ?? tab)?.focus();
    }, 300);
  };

  useEffect(() => {
    if (!routeSelectedTab) return;

    setLocalStorageFlag(
      "bandForecastingTab",
      routeSelectedTab === BANDWIDTH_FORECASTING_TAB,
    );
    setSelectedTab(routeSelectedTab);

    if (props.globalFilter.predictiveTabSelected !== routeSelectedTab) {
      props.setAltoProps({ predictiveTabSelected: routeSelectedTab, status: "" });
    }
  }, [
    routeSelectedTab,
    props.globalFilter.predictiveTabSelected,
    props.setAltoProps,
  ]);

  useEffect(() => {
    if (!isUIConvergence || routeSelectedTab) return;

    if (isBandwidthTab) {
      setSelectedTab(BANDWIDTH_FORECASTING_TAB);
    } else if (props.globalFilter.predictiveTabSelected === ANOMALY_TAB) {
      setSelectedTab(ANOMALY_TAB);
    } else {
      setSelectedTab(PPR_TAB);
    }
  }, [
    isUIConvergence,
    isBandwidthTab,
    props.globalFilter.predictiveTabSelected,
    routeSelectedTab,
  ]);

  return (
    <div
      className={css[tabStyle]}
      id="predictiveNetworksTab"
      data-cy="predictive-tab"
      data-testid="predictive-tab"
    >
      <div className="title-second-tab-level">
        <div className="tabs">
          {isSecondaryTabsShown ? (
            <HbrTabGroup primary={isUIConvergence ? true : false}>
              {!isPredictivLabelEnabled && pprAccess.available === true && (
                <HbrTab
                  slot="nav"
                  active={selectedTab === PPR_TAB ? true : false}
                  key={PPR_TAB}
                  panel={PPR_TAB}
                  onHbr-click={() => onTabsChange(PPR_TAB)}
                  sentiment={isUIConvergence ? "success" : "info"}
                >
                  {tViews("predictiveNetworks.pprTab")}
                </HbrTab>
              )}
              {bwfAccess.available === true && (
                <HbrTab
                  slot="nav"
                  active={
                    selectedTab === BANDWIDTH_FORECASTING_TAB ? true : false
                  }
                  key={BANDWIDTH_FORECASTING_TAB}
                  panel={BANDWIDTH_FORECASTING_TAB}
                  onHbr-click={() => onTabsChange(BANDWIDTH_FORECASTING_TAB)}
                  sentiment={isUIConvergence ? "success" : "info"}
                >
                  {tViews("predictiveNetworks.bandForecastingTab")}
                </HbrTab>
              )}
              {adAccess.available === true && (
                <HbrTab
                  slot="nav"
                  active={selectedTab === ANOMALY_TAB ? true : false}
                  key={ANOMALY_TAB}
                  panel={ANOMALY_TAB}
                  onHbr-click={() => onTabsChange(ANOMALY_TAB)}
                  sentiment={isUIConvergence ? "success" : "info"}
                >
                  {tViews("predictiveNetworks.anomaly")}
                </HbrTab>
              )}
            </HbrTabGroup>
          ) : (
            <div className="no-tab-title hbr-type-h2">
              {tViews("predictiveNetworks.pprTab")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PredictiveNetworksTabs.propTypes = {
  globalFilter: PropTypes.object,
  setAltoProps: PropTypes.func.isRequired,
};

export default AppHOC(PredictiveNetworksTabs);
